import React, { createContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { Reminder } from '../types';
import { API_BASE_URL } from '../constants';

interface ReminderContextType {
  reminders: Reminder[];
  loading: boolean;
  fetchReminders: () => Promise<void>;
  saveReminder: (reminder: Partial<Reminder>) => Promise<void>;
  deleteReminder: (id: string) => Promise<void>;
  toggleCompleted: (id: string, isCompleted: boolean) => Promise<void>;
}

export const ReminderContext = createContext<ReminderContextType | undefined>(undefined);

export const ReminderProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [reminders, setReminders] = useState<Reminder[]>([]);
    const [loading, setLoading] = useState(true);

    const fetchReminders = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/reminders`);
            if (!response.ok) throw new Error('Failed to fetch reminders');
            const data: Reminder[] = await response.json();
            setReminders(data);
        } catch (error) {
            console.error('Error fetching reminders:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchReminders();
    }, [fetchReminders]);

    const saveReminder = async (reminderData: Partial<Reminder>) => {
        try {
            const isUpdate = !!reminderData.id;
            const url = isUpdate ? `${API_BASE_URL}/reminders/${reminderData.id}` : `${API_BASE_URL}/reminders`;
            const method = isUpdate ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(reminderData),
            });

            if (!response.ok) throw new Error('Failed to save reminder');
            await fetchReminders();
        } catch (error) {
            console.error('Error saving reminder:', error);
            throw error;
        }
    };

    const deleteReminder = async (id: string) => {
        try {
            const response = await fetch(`${API_BASE_URL}/reminders/${id}`, { method: 'DELETE' });
            if (!response.ok) throw new Error('Failed to delete reminder');
            setReminders(prev => prev.filter(r => r.id !== id));
        } catch (error) {
            console.error('Error deleting reminder:', error);
            throw error;
        }
    };

    const toggleCompleted = async (id: string, isCompleted: boolean) => {
        const reminder = reminders.find(r => r.id === id);
        if (reminder) {
            await saveReminder({ ...reminder, isCompleted });
        }
    };

    return (
        <ReminderContext.Provider value={{ reminders, loading, fetchReminders, saveReminder, deleteReminder, toggleCompleted }}>
            {children}
        </ReminderContext.Provider>
    );
};
